import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';

import SearchOutlined from '@ant-design/icons/SearchOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import LeftOutlined from '@ant-design/icons/LeftOutlined';

import { getSkillHierarchy } from 'services/api/skills';
import { GOAL_SERVICES } from 'utils/goalServices';
import { parseLadder, weaveStatement } from 'sections/shared/skill-repo/paramEngine';

const SWATCHES = ['#2563eb', '#0e9f6e', '#b45309', '#a21caf', '#dc2626', '#0891b2', '#7c3aed', '#ca8a04'];
const FRAMEWORKS = ['Native', 'VB-MAPP', 'ABLLS-R', 'MDPS'];

const splitTags = (tags) => {
  const list = Array.isArray(tags) ? tags : [];
  return {
    frameworks: list.filter((t) => typeof t === 'string' && !t.trim().startsWith('[')),
    refs: list.filter((t) => typeof t === 'string' && t.trim().startsWith('['))
  };
};

// Ladder parsing + inline weaving live in the shared paramEngine (see its
// docs): explicit "( )" placeholders and in-text first-ladder-value anchors get
// the chosen value woven INTO the wording; otherwise a criteria clause appends.

/**
 * Repository-driven IEP goal builder (2 steps):
 *   1. Search the Skill Repository (domain + framework filters).
 *   2. Configure the goal — pick a value from each parameter LADDER (the source
 *      its parameters); the wording stays fixed and the chosen values are
 *      woven into a live preview.
 * onAdd receives the assembled goal object (the caller persists it).
 */
const GoalBuilderModal = ({ open, onClose, onAdd, existingCodes = [] }) => {
  const theme = useTheme();
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState('');
  const [activeDomains, setActiveDomains] = useState(new Set());
  const [activeTags, setActiveTags] = useState(new Set());

  // Step-2 state
  const [picked, setPicked] = useState(null); // the chosen skill
  const [goalName, setGoalName] = useState('');
  const [params, setParams] = useState([]); // [{raw, meta, value}]
  const [status, setStatus] = useState('in_progress');
  // Which service works this goal. Seeded from the domain when it names one.
  const [serviceCode, setServiceCode] = useState('aba');
  // Start/End default to BLANK — the author sets the goal's window explicitly
  // (matching the custom GoalDetailsModal), rather than inheriting the whole
  // IEP window as the per-goal window.
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [baseline, setBaseline] = useState('');

  // In-flight guard for "Add to IEP". The parent's onAdd is async (create goal
  // + snapshot mirror — several round-trips) and keeps this modal open until it
  // finishes, so without this a double-click fires onAdd twice and creates the
  // same goal twice (duplicate G-xxx rows). The ref blocks synchronous re-entry
  // before React re-renders; the state disables the buttons for the duration.
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSearch('');
    setActiveDomains(new Set());
    setActiveTags(new Set());
    setPicked(null);
    setStartDate('');
    setEndDate('');
    submittingRef.current = false;
    setSubmitting(false);
    // Refetch every open so repository changes made after the first open (new
    // approvals, imports) are reflected. Cached data renders meanwhile.
    setLoading(hierarchy.length === 0);
    getSkillHierarchy()
      .then((t) => setHierarchy(Array.isArray(t) ? t : []))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Flatten hierarchy -> searchable skills (active only), with domain colour.
  const skills = useMemo(() => {
    const out = [];
    hierarchy.forEach((dom, di) => {
      const color = SWATCHES[di % SWATCHES.length];
      (dom.sub_domains || [])
        // Only live, approved, non-review-bucket sub-domains feed the picker.
        .filter((s) => !s.is_review_bucket && s.is_active !== false && s.approval_status !== 'pending')
        .forEach((sub) => {
          (sub.skills || [])
            .filter((sk) => sk.is_active !== false && sk.approval_status !== 'pending')
            .forEach((sk) => {
              out.push({
                skillId: sk.id,
                name: sk.name,
                params: sk.parameters || [],
                tags: Array.isArray(sk.tags) ? sk.tags : [],
                domainCode: dom.code,
                domainName: dom.name,
                subName: sub.name,
                color
              });
            });
        });
    });
    return out;
  }, [hierarchy]);

  const domainList = useMemo(
    () => hierarchy.map((d, i) => ({ code: d.code, name: d.name, color: SWATCHES[i % SWATCHES.length] })),
    [hierarchy]
  );

  const q = search.trim().toLowerCase();
  const results = useMemo(() => {
    return skills
      .filter((s) => {
        if (activeDomains.size && !activeDomains.has(s.domainCode)) return false;
        if (activeTags.size && !s.tags.some((t) => activeTags.has(t))) return false;
        if (q && !(s.name.toLowerCase().includes(q) || s.subName.toLowerCase().includes(q) || s.tags.join(' ').toLowerCase().includes(q)))
          return false;
        return true;
      })
      .slice(0, 200);
  }, [skills, activeDomains, activeTags, q]);

  const toggle = (set, val, setter) => {
    const n = new Set(set);
    n.has(val) ? n.delete(val) : n.add(val);
    setter(n);
  };

  const choose = (skill) => {
    setPicked(skill);
    setGoalName(skill.name);
    setBaseline('');
    setStatus('in_progress');
    // DEFAULT VALUE: the parameter as the repository holds it. A goal can be
    // added without touching it, and anything here can be rewritten first.
    // Five boxes, always. The goal text can reference {1}..{5}, so a parameter
    // the repository left blank still has to be enterable here.
    const raws = skill.params || [];
    setParams(
      Array.from({ length: 5 }, (_, i) => {
        const raw = String(raws[i] || '').trim();
        const usable = raw && raw.toUpperCase() !== 'NA' ? raw : '';
        return { raw: usable, meta: parseLadder(usable), value: usable };
      })
    );
    // The domain's own service, where it has one. A code like ST-FLUENCY or
    // OT-HANDWRITING answers this without asking; the ABA domains carry no
    // service tag, and ABA is what they are.
    const code = String(skill.domainCode || '').toUpperCase();
    if (code.startsWith('ST-')) setServiceCode('speech_therapy');
    else if (code.startsWith('OT-')) setServiceCode('occupational_therapy');
    else setServiceCode('aba');
    setStep(2);
  };

  // Weave chosen values into the statement (empty values fall back to the
  // ladder's first step — the parameter default).
  const effectiveParams = params.map((p) => ({
    ...p,
    value: (p.value ?? '').toString().trim() || p.meta.raw || ''
  }));
  const woven = weaveStatement(goalName, effectiveParams);
  const statement = woven.statement;
  // Exactly what is in each box. The unit used to be appended to a numeric value
  // here, which re-invented a phrase the clinician had not typed.
  const criteriaVals = effectiveParams.map((p) => (p.value || '').toString().trim()).filter(Boolean);

  const nextCode = () => {
    let max = 0;
    existingCodes.forEach((c) => {
      const m = /(\d+)\s*$/.exec(c || '');
      if (m) max = Math.max(max, parseInt(m[1], 10));
    });
    return `G-${String(max + 1).padStart(3, '0')}`;
  };

  const commit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      // Await the parent's handler (async when it persists the goal) so the
      // button stays disabled until the create fully completes or fails.
      await onAdd({
        goalId: nextCode(),
        domainCode: picked.domainCode,
        domainName: picked.domainName,
        subName: picked.subName,
        name: goalName.trim(),
        statement,
        params: criteriaVals,
        targetCriteria: criteriaVals.join(', '),
        baseline: baseline.trim(),
        startDate,
        endDate,
        status,
        serviceCode,
        tags: picked.tags
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {step === 1 ? 'Add goal from Skill Repository' : 'Configure goal'}
        <IconButton size="small" onClick={onClose} disabled={submitting}>
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 420 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : step === 1 ? (
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              size="small"
              autoFocus
              placeholder="Search goals by wording, sub-domain, framework…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined />
                  </InputAdornment>
                )
              }}
            />
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {domainList.map((d) => (
                <Chip
                  key={d.code}
                  size="small"
                  label={d.name}
                  onClick={() => toggle(activeDomains, d.code, setActiveDomains)}
                  variant={activeDomains.has(d.code) ? 'filled' : 'outlined'}
                  sx={{ borderLeft: `4px solid ${d.color}`, ...(activeDomains.has(d.code) && { bgcolor: alpha(d.color, 0.15) }) }}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {FRAMEWORKS.map((t) => (
                <Chip
                  key={t}
                  size="small"
                  label={t}
                  color={activeTags.has(t) ? 'primary' : 'default'}
                  variant={activeTags.has(t) ? 'filled' : 'outlined'}
                  onClick={() => toggle(activeTags, t, setActiveTags)}
                />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {results.length} matching goal{results.length === 1 ? '' : 's'}
              {results.length === 200 ? '+ (refine your search)' : ''}
            </Typography>
            <Paper variant="outlined" sx={{ borderRadius: 2, maxHeight: 340, overflow: 'auto' }}>
              {results.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No goals match. Adjust the filters or search.
                  </Typography>
                </Box>
              ) : (
                results.map((s, i) => {
                  const { frameworks, refs } = splitTags(s.tags);
                  return (
                    <Box
                      key={s.skillId}
                      onClick={() => choose(s)}
                      sx={{
                        display: 'flex',
                        gap: 1.25,
                        p: 1.5,
                        cursor: 'pointer',
                        borderBottom: i < results.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                      }}
                    >
                      <Box sx={{ width: 4, borderRadius: 2, bgcolor: s.color, flex: 'none' }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2">{s.name}</Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }} alignItems="center">
                          <Chip
                            size="small"
                            label={s.domainName}
                            sx={{ height: 18, fontSize: '0.62rem', bgcolor: s.color, color: '#fff' }}
                          />
                          <Chip size="small" label={s.subName} variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                          {s.params.length > 0 && (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`${s.params.length} parameter${s.params.length > 1 ? 's' : ''}`}
                              sx={{ height: 18, fontSize: '0.62rem' }}
                            />
                          )}
                          {frameworks.map((t, k) => (
                            <Chip key={`f${k}`} size="small" label={t} sx={{ height: 18, fontSize: '0.6rem' }} />
                          ))}
                          {refs.map((r, k) => (
                            <Box
                              key={`r${k}`}
                              component="span"
                              sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.6rem', color: 'text.secondary' }}
                            >
                              {r}
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                      <Typography variant="caption" color="primary.main" sx={{ alignSelf: 'center', fontWeight: 600 }}>
                        Select →
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Paper>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Goal template (editable) — chosen parameters are woven into the statement below
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                value={goalName}
                onChange={(e) => setGoalName(e.target.value)}
                sx={{ mt: 0.5 }}
              />
            </Box>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                Goal preview — parameter values are woven into the wording
              </Typography>
              <Typography variant="body2" component="div">
                {woven.segments.map((seg, i) =>
                  seg.type === 'value' ? (
                    <Box
                      key={i}
                      component="span"
                      sx={{
                        color: 'primary.dark',
                        fontWeight: 700,
                        fontStyle: 'italic',
                        textDecoration: 'underline',
                        textDecorationColor: theme.palette.primary.main,
                        textUnderlineOffset: 3
                      }}
                    >
                      {seg.text}
                    </Box>
                  ) : (
                    <Box key={i} component="span">
                      {seg.text}
                    </Box>
                  )
                )}
              </Typography>
            </Paper>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Skill domain"
                  value={`${picked?.domainName} › ${picked?.subName}`}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <MenuItem value="not_started">Not started</MenuItem>
                  <MenuItem value="in_progress">In progress</MenuItem>
                  <MenuItem value="mastered">Mastered</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Service"
                  value={serviceCode}
                  onChange={(e) => setServiceCode(e.target.value)}
                >
                  {GOAL_SERVICES.map((svc) => (
                    <MenuItem key={svc.code} value={svc.code}>
                      {svc.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start date"
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End date"
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Goal parameters
              </Typography>
            </Divider>
            <Grid container spacing={1.5}>
              {params.map((p, idx) => (
                <Grid key={idx} size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={3}
                    label={`Parameter ${idx + 1}`}
                    value={p.value}
                    onChange={(e) => setParams((prev) => prev.map((x, i) => (i === idx ? { ...x, value: e.target.value } : x)))}
                  />
                </Grid>
              ))}
            </Grid>

            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Current baseline"
              placeholder="Describe the child's current performance for this goal."
              value={baseline}
              onChange={(e) => setBaseline(e.target.value)}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {step === 2 && (
            <Button startIcon={<LeftOutlined />} onClick={() => setStep(1)} disabled={submitting}>
              Back to search
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }} disabled={submitting}>
            Cancel
          </Button>
          {step === 2 && (
            <Button
              variant="contained"
              onClick={commit}
              disabled={!goalName.trim() || submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {submitting ? 'Adding…' : 'Add to IEP'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

GoalBuilderModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  existingCodes: PropTypes.array
};

export default GoalBuilderModal;
